
import React, { useState, useEffect, useRef } from 'react';
import './AnalyticsPage.css';
import PageHeader from '../../components/shared/PageHeader';
import Card from '../../components/shared/Card';
import Button from '../../components/shared/Button';
import Badge from '../../components/shared/Badge';
import KpiTile from '../../components/shared/KpiTile';
import { useToast } from '../../context/ToastContext';

type Period = '7d' | '30d' | '90d' | '1y';
type ChartTab = 'revenue' | 'users' | 'sessions' | 'conversion';

const PERIODS: { id: Period; label: string }[] = [
  { id: '7d', label: '7 Days' },
  { id: '30d', label: '30 Days' },
  { id: '90d', label: '90 Days' },
  { id: '1y', label: '1 Year' },
];

const CHART_TABS: { id: ChartTab; label: string; color: string }[] = [
  { id: 'revenue', label: 'Revenue', color: '#1282D6' },
  { id: 'users', label: 'Users', color: '#22C55E' },
  { id: 'sessions', label: 'Sessions', color: '#8B5CF6' },
  { id: 'conversion', label: 'Conversion', color: '#F59E0B' },
];

function generateData(points: number, base: number, variance: number): number[] {
  let val = base;
  return Array.from({ length: points }, () => {
    val += (Math.random() - 0.46) * variance;
    val = Math.max(base * 0.3, val);
    return Math.round(val);
  });
}

const DATA_SETS: Record<Period, Record<ChartTab, number[]>> = {
  '7d': {
    revenue: generateData(7, 6800, 1200),
    users: generateData(7, 420, 80),
    sessions: generateData(7, 1100, 200),
    conversion: generateData(7, 38, 8),
  },
  '30d': {
    revenue: generateData(30, 6200, 900),
    users: generateData(30, 390, 60),
    sessions: generateData(30, 980, 150),
    conversion: generateData(30, 36, 6),
  },
  '90d': {
    revenue: generateData(90, 5800, 700),
    users: generateData(90, 360, 50),
    sessions: generateData(90, 920, 120),
    conversion: generateData(90, 34, 5),
  },
  '1y': {
    revenue: generateData(52, 5400, 800),
    users: generateData(52, 340, 55),
    sessions: generateData(52, 890, 130),
    conversion: generateData(52, 32, 6),
  },
};

const TOP_PAGES = [
  { path: '/dashboard', views: 24810, bounce: '18%', avg: '4m 32s' },
  { path: '/business/customers', views: 18340, bounce: '22%', avg: '3m 15s' },
  { path: '/studio/ai-hub', views: 14920, bounce: '12%', avg: '6m 48s' },
  { path: '/business/money', views: 11200, bounce: '28%', avg: '2m 50s' },
  { path: '/ops/monitor', views: 8760, bounce: '31%', avg: '2m 10s' },
];

const TRAFFIC_SOURCES = [
  { source: 'Direct', value: 38, color: '#1282D6' },
  { source: 'Organic Search', value: 27, color: '#22C55E' },
  { source: 'Referral', value: 18, color: '#8B5CF6' },
  { source: 'Social', value: 11, color: '#F59E0B' },
  { source: 'Email', value: 6, color: '#EF4444' },
];

const DEVICES = [
  { type: 'Desktop', pct: 58, icon: '🖥' },
  { type: 'Mobile', pct: 31, icon: '📱' },
  { type: 'Tablet', pct: 11, icon: '📟' },
];

interface ChartPoint {
  value: number;
  label: string;
  index: number;
}

function SparkChart({
  data,
  color,
  height = 200,
  showArea = true,
  showGrid = true,
  formatValue,
}: {
  data: number[];
  color: string;
  height?: number;
  showArea?: boolean;
  showGrid?: boolean;
  formatValue?: (v: number) => string;
}) {
  const [hovered, setHovered] = useState<ChartPoint | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const padX = 8;
  const padY = 16;
  const w = 600;
  const h = height;

  const xs = data.map((_, i) => padX + (i / (data.length - 1)) * (w - padX * 2));
  const ys = data.map(v => padY + (1 - (v - min) / range) * (h - padY * 2));

  const linePath = xs.map((x, i) => `${i === 0 ? 'M' : 'L'} ${x} ${ys[i]}`).join(' ');
  const areaPath = `${linePath} L ${xs[xs.length - 1]} ${h} L ${xs[0]} ${h} Z`;

  const gradId = `grad-${color.replace('#', '')}`;

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const relX = ((e.clientX - rect.left) / rect.width) * w;
    let closest = 0;
    let minDist = Infinity;
    xs.forEach((x, i) => {
      const d = Math.abs(x - relX);
      if (d < minDist) { minDist = d; closest = i; }
    });
    setHovered({ value: data[closest], label: `Point ${closest + 1}`, index: closest });
  };

  return (
    <div className="spark-chart" style={{ height }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        className="spark-chart__svg"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHovered(null)}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0.01" />
          </linearGradient>
        </defs>
        {showGrid && [0.25, 0.5, 0.75].map(t => (
          <line
            key={t}
            x1={padX}
            y1={padY + t * (h - padY * 2)}
            x2={w - padX}
            y2={padY + t * (h - padY * 2)}
            stroke="rgba(255,255,255,0.04)"
            strokeWidth="1"
          />
        ))}
        {showArea && (
          <path d={areaPath} fill={`url(#${gradId})`} />
        )}
        <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {hovered !== null && (
          <>
            <line
              x1={xs[hovered.index]}
              y1={padY}
              x2={xs[hovered.index]}
              y2={h - padY}
              stroke={color}
              strokeWidth="1"
              strokeDasharray="4 3"
              strokeOpacity="0.5"
            />
            <circle cx={xs[hovered.index]} cy={ys[hovered.index]} r="5" fill={color} stroke="#111827" strokeWidth="2" />
          </>
        )}
      </svg>
      {hovered !== null && (
        <div
          className="spark-chart__tooltip"
          style={{
            left: `${(xs[hovered.index] / w) * 100}%`,
            borderColor: color,
            color,
          }}
        >
          {formatValue ? formatValue(hovered.value) : hovered.value.toLocaleString()}
        </div>
      )}
    </div>
  );
}

function DonutChart({ segments }: { segments: typeof TRAFFIC_SOURCES }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const total = segments.reduce((a, b) => a + b.value, 0);
  const cx = 80, cy = 80, r = 60, inner = 36;
  let angle = -90;

  const arcs = segments.map((seg, i) => {
    const sweep = (seg.value / total) * 360;
    const start = angle;
    angle += sweep;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const x1 = cx + r * Math.cos(toRad(start));
    const y1 = cy + r * Math.sin(toRad(start));
    const x2 = cx + r * Math.cos(toRad(start + sweep));
    const y2 = cy + r * Math.sin(toRad(start + sweep));
    const xi1 = cx + inner * Math.cos(toRad(start));
    const yi1 = cy + inner * Math.sin(toRad(start));
    const xi2 = cx + inner * Math.cos(toRad(start + sweep));
    const yi2 = cy + inner * Math.sin(toRad(start + sweep));
    const large = sweep > 180 ? 1 : 0;
    const path = `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${xi2} ${yi2} A ${inner} ${inner} 0 ${large} 0 ${xi1} ${yi1} Z`;
    return { ...seg, path, index: i };
  });

  return (
    <div className="donut-chart">
      <svg viewBox="0 0 160 160" className="donut-chart__svg">
        {arcs.map(arc => (
          <path
            key={arc.source}
            d={arc.path}
            fill={arc.color}
            opacity={hovered === null || hovered === arc.index ? 1 : 0.35}
            style={{ transition: 'opacity .2s', cursor: 'pointer' }}
            onMouseEnter={() => setHovered(arc.index)}
            onMouseLeave={() => setHovered(null)}
          />
        ))}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="16" fontWeight="800" fill="#E5E7EB">
          {hovered !== null ? segments[hovered].value + '%' : total + '%'}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize="9" fill="#94A3B8">
          {hovered !== null ? segments[hovered].source : 'Traffic'}
        </text>
      </svg>
      <div className="donut-legend">
        {segments.map((s, i) => (
          <div
            key={s.source}
            className={`donut-legend__item${hovered === i ? ' donut-legend__item--active' : ''}`}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          >
            <span className="donut-legend__dot" style={{ background: s.color }} />
            <span className="donut-legend__label">{s.source}</span>
            <span className="donut-legend__val">{s.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const [period, setPeriod] = useState<Period>('30d');
  const [chartTab, setChartTab] = useState<ChartTab>('revenue');
  const { addToast } = useToast();

  const data = DATA_SETS[period][chartTab];
  const activeColor = CHART_TABS.find(t => t.id === chartTab)?.color ?? '#1282D6';
  const total = data.reduce((a, b) => a + b, 0);
  const last = data[data.length - 1];
  const prev = data[data.length - 2] || last;
  const delta = prev ? (((last - prev) / prev) * 100).toFixed(1) : '0';
  const deltaUp = last >= prev;

  const formatValue = (v: number) => {
    if (chartTab === 'revenue') return `$${v.toLocaleString()}`;
    if (chartTab === 'conversion') return `${v}%`;
    return v.toLocaleString();
  };

  return (
    <div className="analytics-page">
      <PageHeader
        title="Analytics"
        breadcrumb={['Business', 'Analytics']}
        actions={
          <>
            <div className="analytics-period-switcher">
              {PERIODS.map(p => (
                <button
                  key={p.id}
                  className={`analytics-period-btn${period === p.id ? ' analytics-period-btn--active' : ''}`}
                  onClick={() => setPeriod(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => addToast('info', 'Exporting report…')}>
              Export
            </Button>
          </>
        }
      />

      {/* KPIs */}
      <div className="analytics-kpi-grid">
        <KpiTile label="Total Revenue" value="$48,291" delta="12.4%" deltaUp icon="💰" accent="var(--color-success)" />
        <KpiTile label="Active Users" value="1,284" delta="3.2%" deltaUp icon="👥" accent="var(--color-ocean-500)" />
        <KpiTile label="Avg. Sessions" value="9,410" delta="28%" deltaUp icon="📈" accent="var(--color-purple)" />
        <KpiTile label="Conversion Rate" value="36.8%" delta="1.5%" deltaUp={false} icon="🎯" accent="var(--color-warning)" />
      </div>

      {/* Main Chart */}
      <Card
        title="Performance Over Time"
        actions={
          <div className="chart-tab-bar">
            {CHART_TABS.map(t => (
              <button
                key={t.id}
                className={`chart-tab${chartTab === t.id ? ' chart-tab--active' : ''}`}
                style={chartTab === t.id ? { borderColor: t.color, color: t.color } : {}}
                onClick={() => setChartTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        }
      >
        <div className="analytics-chart-summary">
          <div>
            <span className="chart-summary-label">Total ({period})</span>
            <div className="chart-summary-value">{formatValue(total)}</div>
          </div>
          <div>
            <span className="chart-summary-label">Latest</span>
            <div className="chart-summary-value">{formatValue(last)}</div>
          </div>
          <div>
            <span className="chart-summary-label">Change</span>
            <div className={`chart-summary-delta${deltaUp ? ' chart-summary-delta--up' : ' chart-summary-delta--down'}`}>
              {deltaUp ? '↑' : '↓'} {Math.abs(Number(delta))}%
            </div>
          </div>
        </div>
        <SparkChart data={data} color={activeColor} height={220} formatValue={formatValue} />
      </Card>

      {/* Row 3: Donut + Top Pages + Devices */}
      <div className="analytics-row3">
        <Card title="Traffic Sources">
          <DonutChart segments={TRAFFIC_SOURCES} />
        </Card>

        <Card title="Top Pages" actions={<Badge variant="ocean">{TOP_PAGES.length}</Badge>}>
          <div className="top-pages">
            {TOP_PAGES.map((p, i) => (
              <div key={i} className="top-page-row">
                <span className="top-page-rank">#{i + 1}</span>
                <div className="top-page-info">
                  <span className="top-page-path">{p.path}</span>
                  <div className="top-page-bar-wrap">
                    <div className="top-page-bar" style={{ width: `${(p.views / TOP_PAGES[0].views) * 100}%` }} />
                  </div>
                </div>
                <div className="top-page-stats">
                  <span>{p.views.toLocaleString()}</span>
                  <span className="top-page-muted">Bounce: {p.bounce}</span>
                  <span className="top-page-muted">Avg: {p.avg}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Devices">
          <div className="devices-list">
            {DEVICES.map(d => (
              <div key={d.type} className="device-row">
                <span className="device-icon">{d.icon}</span>
                <div className="device-info">
                  <div className="device-label-row">
                    <span className="device-type">{d.type}</span>
                    <span className="device-pct">{d.pct}%</span>
                  </div>
                  <div className="device-bar-bg">
                    <div
                      className="device-bar-fill"
                      style={{
                        width: `${d.pct}%`,
                        background: d.type === 'Desktop' ? 'var(--color-ocean-500)' : d.type === 'Mobile' ? 'var(--color-success)' : 'var(--color-purple)',
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="devices-realtime">
            <div className="realtime-dot" />
            <span>Live: <strong>247 active users</strong></span>
          </div>
        </Card>
      </div>
    </div>
  );
}
